import { useCallback, useState, type ReactNode } from "react";
import { ArrowDownUp, LayoutGrid, Rows3 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { raiseAmountValue, type Application } from "./deal-fields";

/*
 * How a visitor browses a collection of companies, the same way on every
 * public board: a heading with its count, the page's filters, a sort, and a
 * List / Grid switch that is remembered per page type.
 */

export type ViewMode = "list" | "grid";

/** List or grid, remembered in this browser per kind of page. */
export function useViewMode(
  key: string,
  initial: ViewMode = "list"
): [ViewMode, (mode: ViewMode) => void] {
  const storageKey = `public-view:${key}`;
  const [mode, setMode] = useState<ViewMode>(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      return stored === "grid" || stored === "list" ? stored : initial;
    } catch {
      return initial;
    }
  });

  const set = useCallback(
    (next: ViewMode) => {
      setMode(next);
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // Storage blocked: the choice still holds for this visit.
      }
    },
    [storageKey]
  );

  return [mode, set];
}

export type SortKey = "endorsements" | "newest" | "raise" | "name";

export const SORT_LABELS: Record<SortKey, string> = {
  endorsements: "Most endorsed",
  newest: "Newest",
  raise: "Largest raise",
  name: "A to Z",
};

/** Client-side ordering for boards that hold their whole list in memory. */
export function sortApplications(
  applications: Application[],
  key: SortKey
): Application[] {
  const list = [...applications];
  const name = (a: Application) =>
    (a.applicant_company as { company_name?: string } | undefined)?.company_name ?? "";
  const time = (a: Application) =>
    new Date((a as { created_on?: string }).created_on ?? 0).getTime();

  switch (key) {
    case "endorsements":
      return list.sort((a, b) => (b.upvote_count ?? 0) - (a.upvote_count ?? 0));
    case "newest":
      return list.sort((a, b) => time(b) - time(a));
    case "raise":
      return list.sort(
        (a, b) => (raiseAmountValue(b) ?? -1) - (raiseAmountValue(a) ?? -1)
      );
    case "name":
      return list.sort((a, b) => name(a).localeCompare(name(b)));
  }
}

/** A sort, remembered for the visit. */
export function useSort(initial: SortKey = "endorsements") {
  return useState<SortKey>(initial);
}

export function ViewToggle({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
}) {
  const options: Array<{ mode: ViewMode; label: string; icon: ReactNode }> = [
    { mode: "list", label: "List view", icon: <Rows3 className="h-4 w-4" /> },
    { mode: "grid", label: "Grid view", icon: <LayoutGrid className="h-4 w-4" /> },
  ];
  return (
    <div
      role="group"
      aria-label="Layout"
      className="inline-flex h-9 items-center rounded-lg border border-border bg-card p-0.5"
    >
      {options.map((o) => (
        <button
          key={o.mode}
          type="button"
          aria-label={o.label}
          aria-pressed={value === o.mode}
          title={o.label}
          onClick={() => onChange(o.mode)}
          className={cn(
            "inline-flex h-full w-9 items-center justify-center rounded-md transition-colors duration-150",
            value === o.mode
              ? "bg-foreground text-background"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          {o.icon}
        </button>
      ))}
    </div>
  );
}

export function SortSelect({
  value,
  onChange,
  options = ["endorsements", "newest", "raise", "name"],
}: {
  value: SortKey;
  onChange: (key: SortKey) => void;
  options?: SortKey[];
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as SortKey)}>
      <SelectTrigger
        aria-label="Sort"
        className="h-9 w-auto gap-2 rounded-lg border-border bg-card px-3 text-[13px] [&>svg:last-child]:opacity-60"
      >
        <ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end">
        {options.map((key) => (
          <SelectItem key={key} value={key} className="text-[13px]">
            {SORT_LABELS[key]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * The collection's header: title and count on the left, sort and layout on
 * the right, then the page's filters on their own row so any number of them
 * fit without crowding the title.
 */
export function CollectionToolbar({
  title,
  count,
  filters,
  sort,
  view,
  className,
}: {
  title: ReactNode;
  count?: number;
  /** The page's filter controls (multi-selects, search). */
  filters?: ReactNode;
  /** A <SortSelect/>, or whatever ordering control the page offers. */
  sort?: ReactNode;
  /** A <ViewToggle/>. */
  view?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("pb-5", className)}>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <h2 className="text-[clamp(1.5rem,1.25rem+0.9vw,2rem)] font-semibold leading-none tracking-[-0.03em]">
          {title}
          {count !== undefined && (
            <span className="ml-3 align-top text-[0.45em] font-medium tabular-nums tracking-normal text-muted-foreground">
              {String(count).padStart(2, "0")}
            </span>
          )}
        </h2>
        {(sort || view) && (
          <div className="flex items-center gap-2">
            {sort}
            {view}
          </div>
        )}
      </div>
      {filters && (
        <div className="-mx-5 mt-5 flex w-[calc(100%+2.5rem)] items-center gap-2 overflow-x-auto px-5 pb-1 [scrollbar-width:none] sm:mx-0 sm:w-auto sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0 [&>*]:shrink-0">
          {filters}
        </div>
      )}
    </div>
  );
}

/**
 * Grid layout: cells share hairline borders like a ledger rather than
 * floating as separate cards, so the grid reads as one object. Each cell draws
 * its own bottom and right rule; the wrapper clips the last column's right
 * rule, so a short last row simply ends on paper.
 */
export function BoardGrid({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className="overflow-hidden border-t border-foreground">
      <ol
        className={cn(
          "-mr-px grid sm:grid-cols-2 lg:grid-cols-3 [&>li]:border-b [&>li]:border-border sm:[&>li]:border-r",
          className
        )}
      >
        {children}
      </ol>
    </div>
  );
}
